import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react'
import { Pressable, View, type LayoutChangeEvent } from 'react-native'
import Animated, {
  Extrapolation,
  interpolate,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated'
import Icon from '~/components/Icon'
import { Label } from '~/components/typography/Content'
import designTokens from '~/lib/designTokens'
import { cn } from '~/lib/utils'

interface CollapsibleContextType {
  isOpen: boolean
  toggle: () => void
  setIsOpen: (open: boolean) => void
}

const CollapsibleContext = createContext<CollapsibleContextType | undefined>(undefined)

const useCollapsibleContext = () => {
  const context = useContext(CollapsibleContext)
  if (!context) {
    // eslint-disable-next-line i18next/no-literal-string
    throw new Error('Collapsible components must be used within a Collapsible')
  }
  return context
}

interface CollapsibleProps {
  children: React.ReactNode
  isOpen?: boolean
  onOpenChange?: (open: boolean) => void
  className?: string
  ref?: React.Ref<View>
}

const Collapsible = ({
  children,
  isOpen = false,
  onOpenChange,
  className,
  ref,
  ...props
}: CollapsibleProps) => {
  const [isOpenState, setIsOpenState] = useState(isOpen)
  const currentOpenState = onOpenChange ? isOpen : isOpenState

  const setIsOpen = useCallback(
    (open: boolean) => {
      if (onOpenChange) {
        onOpenChange(open)
      } else {
        setIsOpenState(open)
      }
    },
    [onOpenChange]
  )

  const toggle = useCallback(() => {
    const next = !currentOpenState
    setIsOpen(next)
  }, [currentOpenState, setIsOpen])

  const contextValue: CollapsibleContextType = {
    isOpen: currentOpenState,
    toggle,
    setIsOpen,
  }

  return (
    <CollapsibleContext.Provider value={contextValue}>
      <View ref={ref} className={cn('overflow-hidden', className)} {...props}>
        {children}
      </View>
    </CollapsibleContext.Provider>
  )
}

Collapsible.displayName = 'Collapsible'

interface CollapsibleTriggerProps {
  children: ReactNode
  className?: string
  labelClassName?: string
  asChild?: boolean
  ref?: React.Ref<View>
}

const CollapsibleTrigger = ({
  children,
  className,
  labelClassName,
  asChild = false,
  ref,
  ...props
}: CollapsibleTriggerProps) => {
  const { toggle, isOpen } = useCollapsibleContext()

  if (asChild && React.isValidElement(children)) {
    return React.cloneElement(children as React.JSX.Element, {
      onPress: toggle,
      ...(children as React.JSX.Element).props,
    })
  }

  return (
    <Pressable
      ref={ref}
      onPress={toggle}
      className={cn('flex-row items-center justify-center gap-3', className)}
      {...props}>
      {typeof children === 'string' ? (
        <>
          <Label className={cn('underline', labelClassName)}>{children}</Label>
          <Icon
            name={isOpen ? 'chevron-up-outline' : 'chevron-down-outline'}
            size={20}
            color={designTokens.foreground}
          />
        </>
      ) : (
        children
      )}
    </Pressable>
  )
}

CollapsibleTrigger.displayName = 'CollapsibleTrigger'

interface CollapsibleContentProps {
  children: React.ReactNode
  className?: string
  wrapperClassName?: string
  duration?: number
  ref?: React.Ref<View>
}

const useCollapsibleProgress = (duration: number) => {
  const { isOpen } = useCollapsibleContext()
  // Animation progress: 0 -> collapsed, 1 -> expanded
  const progress = useSharedValue(1)

  useEffect(() => {
    if (isOpen) {
      progress.value = withTiming(1, { duration })
    } else {
      progress.value = withTiming(0, { duration })
    }
  }, [isOpen, duration, progress])

  return progress
}

const CollapsibleContent = ({
  children,
  className,
  wrapperClassName,
  duration = 300,
  ref,
  ...props
}: CollapsibleContentProps) => {
  const [contentHeight, setContentHeight] = useState(0)
  const progress = useCollapsibleProgress(duration)

  const onLayout = useCallback(
    (event: LayoutChangeEvent) => {
      const { height } = event.nativeEvent.layout

      if (height > 0 && contentHeight !== height) {
        setContentHeight(height)
      }
    },
    [contentHeight]
  )

  const animatedStyle = useAnimatedStyle(() => {
    return {
      height: contentHeight * progress.value,
    }
  })

  const contentStyle = useAnimatedStyle(() => {
    const opacity = interpolate(progress.value, [0, 1], [0, 1], Extrapolation.CLAMP)

    return {
      opacity,
    }
  })

  return (
    <Animated.View
      ref={ref}
      style={animatedStyle}
      className={cn('overflow-hidden', wrapperClassName)}
      {...props}>
      <Animated.View
        style={contentStyle}
        onLayout={onLayout}
        className={cn('absolute left-0 right-0 top-0', className)}>
        {children}
      </Animated.View>
    </Animated.View>
  )
}

CollapsibleContent.displayName = 'CollapsibleContent'

type CollapsiblePreviewProps = {
  children: React.ReactNode
  className?: string
  height?: number
  duration?: number
}

const CollapsiblePreview = ({
  children,
  className,
  height = 25,
  duration = 300,
}: CollapsiblePreviewProps) => {
  const progress = useCollapsibleProgress(duration)

  const animatedStyle = useAnimatedStyle(() => {
    return {
      opacity: 1 - progress.value,
      maxHeight: height * (1 - progress.value),
    }
  })

  return (
    <Animated.View style={animatedStyle} className={cn(className)}>
      {children}
    </Animated.View>
  )
}

export { Collapsible, CollapsibleTrigger, CollapsibleContent, CollapsiblePreview }
